import { apiFetch } from "@/lib/api";
import type { InventoryAmlMpnAlternate } from "@/lib/inventoryAmlMpn";
import type { InboundArrivalUnitKind } from "@/pages/shipping/inboundArrivalUnitKind";
import type {
  WarehouseHubLocation,
  WarehouseRecountLocationSummary,
  WarehouseRecountShelfSublocation,
} from "@/lib/warehouseRecountApi";

export type InventoryBox = {
  id: string;
  sku: string;
  quantity: number;
  printed_quantity: number;
  location_id: number | null;
  sublocation: string;
  sublocation_label: string;
  unit_kind: InboundArrivalUnitKind;
  approved_component_id: string | null;
  origin_kind: string | null;
  origin_ref: string | null;
  build_order_id: number | null;
  archived_at: string | null;
  combined_into_id: string | null;
  combined_into_hex: string | null;
  split_from_id: string | null;
  hex: string;
  short_code: string;
  mv_description: string;
  mv_variant: string;
  scan_url: string;
  floor_station_id: number | null;
  location_name: string | null;
  floor_station_name: string | null;
  receiving_stage?: "holding" | "to_stock" | "shelf" | null;
  saved_mpn_label: string | null;
  kitting_pick?: {
    run_id: number;
    slot_key: string;
    run_status: string;
  } | null;
};

export type InventoryCartonAdjustmentRequest = {
  id: string;
  inventory_box_id: string;
  box_hex: string;
  box_short_code: string;
  sku: string;
  quantity_before: number;
  quantity_after: number;
  reason: string;
  request_kind: "quantity" | "consume";
  approved_component_id: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled" | "superseded";
  requested_by_user_id: string;
  requested_by_display_name: string | null;
  requested_at: string;
  resolved_by_user_id: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  mv_document_id: number | null;
};

export type InventoryBoxPutawayContext = {
  box: InventoryBox;
  warehouse_locations: WarehouseHubLocation[];
  sku_locations: WarehouseRecountLocationSummary[];
  shelf_sublocations: WarehouseRecountShelfSublocation[];
  hub_location_id: number;
  location_name: string | null;
};

export type InventoryBoxEvent = {
  id: string;
  event_kind:
    | "created"
    | "quantity"
    | "reprint"
    | "split"
    | "combine"
    | "putaway"
    | "release_to_floor"
    | "consume";
  quantity_before: number | null;
  quantity_after: number | null;
  mv_document_id: number | null;
  related_hex: string | null;
  related_short_code: string | null;
  detail: string | null;
  actor_display_name: string | null;
  actor_avatar_url: string | null;
  created_at: string;
};

export type InventoryBoxSplitPart = {
  quantity: number;
  unit_kind: InboundArrivalUnitKind;
};

export async function fetchInventoryBox(boxId: string): Promise<InventoryBox> {
  const data = await fetchInventoryBoxWithAml(boxId);
  return data.box;
}

export async function fetchInventoryBoxWithAml(
  boxId: string,
  options?: { omitAml?: boolean; omitPending?: boolean },
): Promise<{
  box: InventoryBox;
  aml_mpn_alternates: InventoryAmlMpnAlternate[];
  pending_adjustment_request: InventoryCartonAdjustmentRequest | null;
}> {
  const params = new URLSearchParams();
  if (options?.omitAml === true) {
    params.set("omit_aml", "1");
  }
  if (options?.omitPending === true) {
    params.set("omit_pending", "1");
  }
  const query = params.toString();
  const path =
    `/api/inventory-boxes/${encodeURIComponent(boxId)}`
    + (query !== "" ? `?${query}` : "");
  const data = await apiFetch<{
    box: InventoryBox;
    aml_mpn_alternates: InventoryAmlMpnAlternate[];
    pending_adjustment_request: InventoryCartonAdjustmentRequest | null;
  }>(path, { priority: "interactive" });
  return data;
}

export async function fetchInventoryBoxAmlAlternates(
  boxId: string,
): Promise<{ aml_mpn_alternates: InventoryAmlMpnAlternate[] }> {
  return apiFetch<{ aml_mpn_alternates: InventoryAmlMpnAlternate[] }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/aml-mpn-alternates`,
  );
}

export async function fetchInventoryBoxPendingAdjustment(
  boxId: string,
): Promise<{ pending_adjustment_request: InventoryCartonAdjustmentRequest | null }> {
  return apiFetch<{ pending_adjustment_request: InventoryCartonAdjustmentRequest | null }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/pending-adjustment`,
  );
}

export type InventoryBoxPutawayContextScope = "full" | "sites" | "shelves";

export async function fetchInventoryBoxPutawayContext(
  boxId: string,
  hubLocationId: number,
  scope: InventoryBoxPutawayContextScope = "full",
): Promise<InventoryBoxPutawayContext> {
  const params = new URLSearchParams({
    hub_location_id: String(hubLocationId),
    scope,
  });
  const data = await apiFetch<{ context: InventoryBoxPutawayContext }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/putaway-context?${params.toString()}`,
  );
  return data.context;
}

export async function saveInventoryBoxPutaway(
  boxId: string,
  hubLocationId: number,
  sublocation: string,
): Promise<{ box: InventoryBox; context: InventoryBoxPutawayContext }> {
  return apiFetch<{ box: InventoryBox; context: InventoryBoxPutawayContext }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/putaway`,
    {
      method: "POST",
      body: {
        hub_location_id: hubLocationId,
        sublocation,
      },
    },
  );
}

export async function fetchInventoryBoxEvents(boxId: string): Promise<InventoryBoxEvent[]> {
  const data = await apiFetch<{ events: InventoryBoxEvent[] }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/events`,
  );
  return data.events;
}

export async function saveInventoryBoxQuantity(
  boxId: string,
  quantity: number,
  reason: string,
  approvedComponentId?: string | null,
): Promise<{
  box: InventoryBox;
  pending_adjustment_request: InventoryCartonAdjustmentRequest | null;
}> {
  const body: { quantity: number; reason: string; approved_component_id?: number } = {
    quantity,
    reason,
  };
  if (approvedComponentId != null && approvedComponentId.trim() !== "") {
    body.approved_component_id = Number.parseInt(approvedComponentId, 10);
  }
  return apiFetch<{
    box: InventoryBox;
    pending_adjustment_request: InventoryCartonAdjustmentRequest | null;
  }>(`/api/inventory-boxes/${encodeURIComponent(boxId)}`, { method: "PATCH", body });
}

export async function reprintInventoryBox(boxId: string, printer: string): Promise<InventoryBox> {
  const data = await apiFetch<{ box: InventoryBox }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/reprint`,
    { method: "POST", body: { printer } },
  );
  return data.box;
}

export async function splitInventoryBox(
  boxId: string,
  splits: InventoryBoxSplitPart[],
  printer: string,
): Promise<{ box: InventoryBox; created: InventoryBox[] }> {
  return apiFetch<{ box: InventoryBox; created: InventoryBox[] }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/split`,
    { method: "POST", body: { splits, printer } },
  );
}

export async function combineInventoryBox(
  boxId: string,
  other: string,
): Promise<{ box: InventoryBox; emptied: InventoryBox }> {
  return apiFetch<{ box: InventoryBox; emptied: InventoryBox }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/combine`,
    { method: "POST", body: { other } },
  );
}

export async function consumeInventoryBoxAll(
  boxId: string,
  reason: string,
  quantity: number,
  approvedComponentId?: string | null,
): Promise<{
  box: InventoryBox;
  pending_adjustment_request: InventoryCartonAdjustmentRequest | null;
}> {
  const body: { reason: string; quantity: number; approved_component_id?: number } = {
    reason,
    quantity,
  };
  if (approvedComponentId != null && approvedComponentId.trim() !== "") {
    body.approved_component_id = Number.parseInt(approvedComponentId, 10);
  }
  return apiFetch<{
    box: InventoryBox;
    pending_adjustment_request: InventoryCartonAdjustmentRequest | null;
  }>(`/api/inventory-boxes/${encodeURIComponent(boxId)}/consume-all`, {
    method: "POST",
    body,
  });
}

export async function releaseInventoryBoxToFloor(
  boxId: string,
  stationId: number,
  reason: string,
): Promise<{ box: InventoryBox }> {
  return apiFetch<{ box: InventoryBox }>(
    `/api/inventory-boxes/${encodeURIComponent(boxId)}/release-to-floor`,
    { method: "POST", body: { station_id: stationId, reason } },
  );
}

export function inventoryBoxDescriptionLine(box: InventoryBox): string {
  return [box.mv_description.trim(), box.mv_variant.trim()].filter((part) => part !== "").join(" ");
}

export function inventoryBoxOriginLabel(box: InventoryBox): string | null {
  const ref = box.origin_ref?.trim() ?? "";
  if (box.origin_kind === "work_order") {
    return ref !== "" ? `Work order ${ref}` : "Work order";
  }
  if (box.origin_kind === "receiving") {
    return ref !== "" ? `Receiving ${ref}` : "Receiving";
  }
  if (ref !== "") return ref;
  return null;
}

/** Human label for a unique carton id (short code under the Data Matrix). */
export function inventoryBoxShortLabel(shortCode: string): string {
  const code = shortCode.trim();
  return code !== "" ? `Box ${code}` : "Box";
}
